import { yupResolver } from '@hookform/resolvers/yup'
import { useState, useRef, useEffect, useCallback } from 'react'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { checkErrorYup, handleError } from '../../services/Helper'
import StepAccountDetails from './components/StepAccountDetails'
import StepVerification from './components/StepVerification'
import StepEmailVerified from './components/StepEmailVerified'
import { useRegister } from './Context'
import RegisterSchema from './schema'
import TermsConditions from './components/TermsConditions'
import { clearRegisterDraft } from './draft'

function RegisterForm({ activeStep, setActiveStep }) {
  const { register, verifyOtp, resendOtp, setCookie, cookie } = useRegister()
  const nav = useNavigate()
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  const [otpMode, setOtpMode] = useState(false)
  const numberOfDigits = 4
  const [otp, setOtp] = useState(new Array(numberOfDigits).fill(''))
  const otpBoxReference = useRef([])

  const handleOtpChange = useCallback(
    (value, index) => {
      const normalizedValue = value.replace(/\D/, '')
      const newArr = [...otp]
      newArr[index] = normalizedValue
      setOtp(newArr)

      if (normalizedValue && index < numberOfDigits - 1) {
        otpBoxReference.current[index + 1]?.focus()
      }
    },
    [otp]
  )

  const handleOtpBackspaceAndEnter = useCallback((e, index) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      otpBoxReference.current[index - 1]?.focus()
    }
    if (e.key === 'Enter' && e.target.value && index < numberOfDigits - 1) {
      otpBoxReference.current[index + 1]?.focus()
    }
  }, [])


  useEffect(() => {
    if (activeStep) {
      localStorage.setItem('register_active_step', activeStep)
    }
  }, [activeStep])

  // COUNT DOWN
  const [minutes, setMinutes] = useState(0)
  const [seconds, setSeconds] = useState(0)
  const [countdown, setCountdown] = useState(null)

  useEffect(() => {
    if (!countdown) return

    const intervalId = setInterval(() => {
      const now = moment()
      const timeDifference = moment(countdown).diff(now) // ms

      if (timeDifference <= 0) {
        clearInterval(intervalId)
        setMinutes(0)
        setSeconds(0)
        localStorage.removeItem('countdown_to_new_otp')
        return
      }

      const dur = moment.duration(timeDifference)
      setMinutes(dur.minutes())
      setSeconds(dur.seconds())
    }, 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [countdown])

  useEffect(() => {
    const storedCountdown = localStorage.getItem('countdown_to_new_otp')
    if (storedCountdown) {
      setCountdown(storedCountdown)
    }
  }, [])

  useEffect(() => {
    if (countdown) {
      localStorage.setItem('countdown_to_new_otp', countdown)
    }
  }, [countdown])

  const {
    handleSubmit,
    control,
    trigger,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(RegisterSchema),
  })


  // Steps 3 and 4 never read the account form, so gating them on
  // RegisterSchema only created a way to fail silently (see the restore effect
  // above). They are invoked directly; steps 1-2 keep full validation.
  const runStep = async (data) => {
    try {
      if (activeStep === 1) {
        setActiveStep(2)
      } else if (activeStep === 2) {
        const result = await register(data)
        // Resend cooldown starts at the initial send (mirrors Forget-password).
        const nextCountdown = result?.data?.countdown_to_new_otp
        if (nextCountdown) setCountdown(nextCountdown)
        setActiveStep(3)
      } else if (activeStep === 3) {
        const otpCode = otp.join('')
        await verifyOtp(otpCode)
        setActiveStep(4)
      } else if (activeStep === 4) {
        localStorage.removeItem('register_active_step')
        localStorage.removeItem('countdown_to_new_otp')
        const token = cookie['token-backoffice-temp']

        if (token) {
          setCookie('token-backoffice', token, { path: '/' })
          window.location.href = '/register-company-info'
        }
      }
    } catch (err) {
      console.error(err)
    }
  }

  const onSubmit = (event) => {
    if (event && typeof event.preventDefault === 'function') event.preventDefault()
    if (activeStep === 1 || activeStep === 2) return handleSubmit(runStep)(event)
    return runStep()
  }
  return (
    <form id="right" className="flex-1 overflow-y-hidden h-full" onSubmit={onSubmit}>
      {activeStep === 1 && (
        <StepAccountDetails
          control={control}
          errors={errors}
          trigger={trigger}
          watch={watch}
          isSubmitting={isSubmitting}
          nav={nav}
        />
      )}

      {activeStep === 2 && (
        <TermsConditions
          onBack={() => setActiveStep(1)}
          onAccept={() => setActiveStep(3)}
          watch={watch}
          accepted={acceptedTerms}
          setAccepted={setAcceptedTerms}
          isSubmitting={isSubmitting}
        />
      )}

      {activeStep === 3 && (
        <StepVerification
          control={control}
          trigger={trigger}
          watch={watch}
          isSubmitting={isSubmitting}
          onBack={() => {
            // "Back to log in" must LEAVE the wizard, not fall back to the Terms
            // step — that step re-submits register() with the already-created
            // account (fails) and rewrites register_active_step, stranding the
            // OTP resume. Clear the persisted wizard position + countdown so a
            // later /register starts clean.
            clearRegisterDraft()
            nav('/login')
          }}
          otpMode={otpMode}
          setOtpMode={setOtpMode}
          otp={otp}
          setOtp={setOtp}
          handleOtpChange={handleOtpChange}
          handleOtpBackspaceAndEnter={handleOtpBackspaceAndEnter}
          otpBoxReference={otpBoxReference}
          minutes={minutes}
          seconds={seconds}
          setCountdown={setCountdown}
          resendOtp={resendOtp}
        />
      )}

      {activeStep === 4 && (
        <StepEmailVerified
          isSubmitting={isSubmitting}
          onBack={() => {
            // Same exit as step 3: without clearing, register_active_step=4 +
            // the user-id key made the next "Sign up" land back on this screen.
            clearRegisterDraft()
            nav('/login')
          }}
          onContinue={onSubmit}
        />
      )}
    </form>
  )
}

export default RegisterForm
